import { OfferStatus } from '@/api/deal/types';
import type { SelectOption } from '@/design-system';
import { formatOfferStructure, getOfferStatusUi } from '@/pages/deals/shared/common';

export type DealFilter =
  | {
      type: 'select';
      key: string;
      placeholder: string;
      /** Shown while nothing is chosen, e.g. "All sectors"; defaults to "Select <placeholder>". */
      allLabel?: string;
      withSearch: boolean;
      multiple: boolean;
      options?: readonly (SelectOption & { sectorIds?: string[] })[];
    }
  | {
      type: 'slider';
      key: string;
      placeholder: string;
      min?: number;
      max?: number;
    }
  | {
      type: 'contact';
      key: string;
      placeholder: string;
      source: 'seller' | 'introducer';
    };

export const dealsFilters: DealFilter[] = [
  {
    type: 'select',
    key: 'sectors',
    placeholder: 'Sector',
    withSearch: true,
    multiple: true,
    options: [],
  },
  {
    type: 'select',
    key: 'subSectors',
    placeholder: 'Subsector',
    withSearch: true,
    multiple: true,
    options: [],
  },
  { type: 'slider', key: 'availableSize', placeholder: 'Available Size' },
  { type: 'slider', key: 'minInvestment', placeholder: 'Min. Investment' },
  {
    type: 'select',
    key: 'dealStatuses',
    placeholder: 'Open Deals',
    withSearch: false,
    multiple: true,
    options: [],
  },
];

export const personalDealsFilters = dealsFilters.filter((filter) => filter.key !== 'dealStatuses');

export const preIpoCompanyFilters: DealFilter[] = [
  {
    type: 'select',
    key: 'sectors',
    placeholder: 'Sector',
    allLabel: 'All sectors',
    withSearch: true,
    multiple: true,
    options: [],
  },
  {
    type: 'select',
    key: 'subSectors',
    placeholder: 'Sub-Sector',
    allLabel: 'All sub-sectors',
    withSearch: true,
    multiple: true,
    options: [],
  },
  {
    type: 'select',
    key: 'dealStatuses',
    placeholder: 'Deals',
    allLabel: 'All deals',
    withSearch: false,
    multiple: true,
    options: [],
  },
  {
    type: 'select',
    key: 'followed',
    placeholder: 'Followed',
    withSearch: false,
    multiple: false,
    options: [],
  },
  { type: 'slider', key: 'availableSize', placeholder: 'Available Size' },
  { type: 'slider', key: 'minInvestment', placeholder: 'Min. Investment' },
  {
    type: 'select',
    key: 'structures',
    placeholder: 'Structure',
    allLabel: 'All structures',
    withSearch: false,
    multiple: true,
    options: [],
  },
];

/** Retail keeps All / Followed as tabs and Open offers as a switch, so neither is a filter. */
export const retailCompanyFilters = preIpoCompanyFilters.filter(
  (filter) => filter.key !== 'dealStatuses' && filter.key !== 'followed',
);

/** The API returns structures as `{ value: 'Layer1', label: 'Layer1' }`; show "Layer 1". */
export const withStructureLabels = (filters: DealFilter[]): DealFilter[] =>
  filters.map((filter) =>
    filter.type === 'select' && filter.key === 'structures'
      ? {
          ...filter,
          options: (filter.options ?? []).map((option) => ({
            ...option,
            label: formatOfferStructure(String(option.value)),
          })),
        }
      : filter,
  );

export const OFFER_STATUS_OPTIONS = Object.values(OfferStatus).map((value) => ({
  value,
  label: getOfferStatusUi(value).label,
}));

export const companyOffersFilters = [
  { type: 'slider', key: 'marketCap', placeholder: 'Market Cap' },
  { type: 'slider', key: 'availableSize', placeholder: 'Available Size' },
  { type: 'slider', key: 'minInvestment', placeholder: 'Min. Investment' },
  {
    type: 'select',
    key: 'structures',
    placeholder: 'Structure',
    withSearch: false,
    multiple: true,
    options: [],
  },
  {
    type: 'select',
    key: 'statuses',
    placeholder: 'Status',
    withSearch: false,
    multiple: true,
    options: OFFER_STATUS_OPTIONS,
  },
] satisfies readonly DealFilter[];

export const PERSONAL_DEALS_TABS = {
  ALL: 'all-deals',
  FOLLOWED: 'followed-deals',
  MY_PIPELINE: 'my-pipeline',
  MY_SUBMISSIONS: 'my-submissions',
} as const;

export type PersonalDealsTabKey = (typeof PERSONAL_DEALS_TABS)[keyof typeof PERSONAL_DEALS_TABS];

export const personalDealsTabs = [
  { key: PERSONAL_DEALS_TABS.ALL, label: 'All Deals' },
  { key: PERSONAL_DEALS_TABS.MY_PIPELINE, label: 'My Investments' },
  { key: PERSONAL_DEALS_TABS.MY_SUBMISSIONS, label: 'My Submissions' },
] as const;
